/**
 * Collection Vue Component
 * ------------------------------------------------------------------------------
 * Template in sections/collection.liquid
 * 
 */

import Vue from 'vue';
import axios from 'axios';
import debounce from 'lodash-es/debounce';
import Cookies from 'js-cookie';
import VueSlider from 'vue-slider-component';
import ReadMore from '../components/read-more.vue';

import ListingProductItem from './listing-product-item.vue';
import collapsible from './collapsible.vue';

import { app } from '../app.js';
import { getOnionUrl } from '../utils/api';
import { updateUrlParameter, removeParameterFromUrl } from './../utils/helpers';

const API_URL = getOnionUrl();
const COOKIE_PAGINATE_BY = 'paginate_by';
const DEFAULT_PAGINATION = 48;

const PRICE_KEY = 'price_range';
const SIZE_KEY = 'size';
const COLOR_KEY = 'color';
const COLLECTION_KEY = 'collections';
const VENDOR_KEY = 'vendors';

// Sort values need to match shopify admin, so we can use collection.default_sort_by
// https://shopify.dev/docs/themes/liquid/reference/objects/collection#collection-sort_options
const SHOPIFY_SORT_OPTIONS = [
  {
    shopify_key: "manual",
    sort_by: "manual",
    dir: "asc"
  },
  {
    shopify_key: "best-selling", // Coming soon to Onion nav, for now use manual
    sort_by: "manual",
    dir: "asc"
  },
  {
    shopify_key: "title-ascending",
    sort_by: "title",
    dir: "asc"
  },
  {
    shopify_key: "title-descending",
    sort_by: "title",
    dir: "desc"
  },
  {
    shopify_key: "price-ascending",
    sort_by: "price",
    dir: "asc"
  },
  {
    shopify_key: "price-descending",
    sort_by: "price",
    dir: "desc"
  },
  {
    shopify_key: "created-ascending",
    sort_by: "created_at",
    dir: "asc"
  },
  {
    shopify_key: "created-descending",
    sort_by: "created_at",
    dir: "desc"
  }
 
];

export default {
    components: {
        ListingProductItem,
        collapsible,
        VueSlider,
        ReadMore
    },

    props: [
        'collectionHandle',
        'defaultSort',
        'filterConfig',
        'sortOptionLabels'
    ],

    data() {
        return {
            isLoading: false,
            products: [],
            productCount: 0,
            filteredProducts: [],
            sortValue: null,
            filters: {},
            paginateBy: DEFAULT_PAGINATION,   //new
            currentPage: 1,
            pageCount: 0,
            isFilterPanelOpen: false,
            isFilterSidebar: false,
            filterData: [],
            selectedFilters: {},
            priceRange: [],
            minPrice: null,
            maxPrice: null,
            sortBy: null,
            sortOptions: [],
            isSortMobileOpen: false,
            isFilterMobileOpen: false
        }
    },

    computed: {
        priceFilter() {
            return this.filterData.find(filter => filter.key == PRICE_KEY);
          },
      
          priceRangeString() {
            const prices = this.priceRange.map(price => '$' + price);
            return prices.join(' - ');
          },
      
          fileUrl() {
            return app.fileUrl;
          },
      
          priceKey() {
            return PRICE_KEY;
          }
    },
    watch: {
        isFilterPanelOpen(value) {
            if (value) {
                document.querySelector('body').classList.add('no-scroll');
            } else {
                document.querySelector('body').classList.remove('no-scroll');
            }
        }
      },

      mounted() {
        // set mobile filter position
        if(window.matchMedia('(max-width: 768px)').matches) {
          const header  = document.querySelector('#app').getBoundingClientRect();
          const announcement = document.querySelector("#announcement").getBoundingClientRect();
          const announcementHeaderHeight = header.height + announcement.height;

          let collapseFilter = document.querySelectorAll(".collection__sidebar .filter > div");
          collapseFilter.forEach(el => el.style.top = announcementHeaderHeight+'px');
        }
        // Get user pagination preference
        if (Cookies.get(COOKIE_PAGINATE_BY)) {
          this.paginateBy = parseInt(Cookies.get(COOKIE_PAGINATE_BY));
        }
    
        // Set selected filters based on URL params
        const params = new URL(window.location).searchParams;
    
        // Sort
        if (params.get('sort')) {
          this.sortBy = SHOPIFY_SORT_OPTIONS.find(option => option.shopify_key == params.get('sort'));
        }
    
        // Set sort v-model
        if (!this.sortBy) {
          this.sortBy = SHOPIFY_SORT_OPTIONS.find(option => option.shopify_key == this.defaultSort);
        }
    
        // Pagination
        if (params.get('page')) {
          const page = params.get('page').split('|');
          this.paginateBy = page[0];
          this.currentPage = page[1];
        }
    
        // Filters
        if (params.get('filter')) {
          const urlFilters = params.get('filter').split(';');
    
          for (const filter of urlFilters) {
            const split = filter.split('|');
    
            let data = {
              key: split[0]
            }

            this.filterData.push(data); // Need to create skeleton filterData to check isTag to build request data
    
            Vue.set(this.selectedFilters, split[0], split[1].split(','));
          }
        }
    
        this.getProducts();
      },
    methods: {
        updatePaginateBy() {
          Cookies.set(COOKIE_PAGINATE_BY, this.paginateBy, {
            expires: 999
          });
          this.currentPage = 1;
          this.getProducts();
        },

        openSort() {
            this.isSortMobileOpen = !this.isSortMobileOpen;
        },
        closeSort() {
          this.isFilterMobileOpen = !this.isFilterMobileOpen;
        },
    
        updatePage(page) {
          this.currentPage = page;
          this.getProducts();
        },
    
        toggleFilterPanel() {
          this.isFilterPanelOpen = !this.isFilterPanelOpen;
        },

        toggleFilterSidebar() {
          this.isFilterSidebar = !this.isFilterSidebar;

          if(this.isFilterSidebar == true) {
            document.querySelector('.collection__sidebar').classList.add('active');
          } else {
            document.querySelector('.collection__sidebar').classList.remove('active');
          }
        },
    
        toggleFilter(filter) {
          for (const item of this.filterData) {
            if (item.key != filter.key && filter.isTop) {
              item.isOpen = false;
            }
          }
    
          filter.isOpen = !filter.isOpen;
        },
    
        clearAllFilters() {
          for (const filterKey in this.selectedFilters) {
            this.selectedFilters[filterKey] = [];
          }
          this.isFilterPanelOpen = false;
          this.getProducts();
        },
    
        clearFilter(filter) {
          this.selectedFilters[filter.key] = [];
          filter.isOpen = false;
          this.getProducts();
        },
    
        priceUpdated: debounce(function() {
          if (this.priceRange[1] != this.maxPrice || this.priceRange[0] != this.minPrice) {
            this.selectedFilters[PRICE_KEY] = this.priceRange;
          } else {
            this.selectedFilters[PRICE_KEY] = [];
          }
          this.getProducts();
        }, 1000),
    
        getProducts() {
          this.isLoading = true;
    
          let requestData = {
            toolbar: this._getToolbar(),
            filter: this._getFilters()
          };    
          axios.post(API_URL, requestData).then(({ data }) => {

            console.log(data, 'datas');
            this.filterData = [];
            this.sortOptions = [];
    
            this.products = data.products;
            this.productCount = data.summary.total_num_products;
            this.pageCount = data.summary.total_pages;
    
            for (const option of SHOPIFY_SORT_OPTIONS) {
              if (data.toolbar.available.sort_by.includes(option.sort_by) && data.toolbar.available.dir.includes(option.dir) && option.shopify_key != 'created-ascending' && option.shopify_key != 'manual' && option.shopify_key != 'created-descending'   ) {
                this.sortOptions.push(option);
              }
            }

             // Remove current collection from collections filter
             if (this.selectedFilters?.[COLLECTION_KEY]) {
                 this.selectedFilters[COLLECTION_KEY] = this.selectedFilters[COLLECTION_KEY].filter(collection => collection != this.collectionHandle);
             }

             // Convert price range from object to array
             if (this.selectedFilters?.[PRICE_KEY]) {
                 this.selectedFilters[PRICE_KEY] = Object.values(this.selectedFilters[PRICE_KEY]);
             }
            // Set filterData based on filterConfig (from theme settings) and available filters (from Onion Nav)
            for (const filter of this.filterConfig) {
              if (data.filters.available?.[filter.key]) {
                filter.options = data.filters.available[filter.key];
              } else {
                continue;
              }
              // Exclude option values
              if (filter.options && filter.exclusions) {
                let exclusions = filter.exclusions.split(',');
                let options = filter.options;
                filter.options = options.filter(option => !exclusions.includes(option));
              }

              this.filterData.push(filter);
              if (!(this.selectedFilters?.[filter.key])) {
                Vue.set(this.selectedFilters, filter.key, []);
              }
            }

            // Init price slider values
            if (this.priceFilter) {
              this.minPrice = Math.floor(parseFloat(this.priceFilter.options.min));
              this.maxPrice = Math.ceil(parseFloat(this.priceFilter.options.max));
              this.priceRange = this.selectedFilters[PRICE_KEY].length ?
                [
                  Math.floor(parseFloat(this.selectedFilters[PRICE_KEY][0])), 
                  Math.ceil(parseFloat(this.selectedFilters[PRICE_KEY][1]))
                ] : [
                  this.minPrice,
                  this.maxPrice
                ]
            }
    
            window.scrollTo(0, 0);
    
            this._updateUrlParams();
          
          }).catch((error) => {
            console.log(error);
            
          }).finally(() => {
            this.isLoading = false;
          });
        },
    
        _getToolbar() {
          return {
            sort_by: this.sortBy.sort_by,
            dir: this.sortBy.dir,
            products_per_page: parseInt(this.paginateBy),
            page: parseInt(this.currentPage)
          }
        },
    
        _getFilters() {
          // See expected request body sample above
          let filters = {
            
          };
    
          for (const filter of this.filterData) {
            if (this.selectedFilters[filter.key].length) {
              if (filter.key == PRICE_KEY) {
                filters[filter.key] = {
                  min: this.selectedFilters[filter.key][0],
                  max: this.selectedFilters[filter.key][1]
                }
              } else {
                filters[filter.key] = this.selectedFilters[filter.key];
              }
            }
          }
        
          if (this.collectionHandle != "all") {
            if (!(filters?.collections)) filters.collections = [];
              filters.collections.push(this.collectionHandle);
          }
    
          return filters;
        },
    
        _updateUrlParams() {
          // Set URL params to persist filter selections
    
          // Sort
          if (this.sortBy && this.sortBy.shopify_key != this.defaultSort) {
            updateUrlParameter('sort', this.sortBy.shopify_key);
          } else {
            removeParameterFromUrl('sort');
          }
    
          // Pagination
          if (this.currentPage > 1) {
            updateUrlParameter('page', `${this.paginateBy}|${this.currentPage}`);
          } else {
            removeParameterFromUrl('page');
          }

    
          // Filters
          let filtersArray = [];
          for (const filter of this.filterData) {
            if (this.selectedFilters[filter.key].length) {
              let filterString = `${filter.key}|${this.selectedFilters[filter.key]}`;
              filtersArray.push(filterString);
            }
          }
    
          if (filtersArray.length) {
            updateUrlParameter('filter', filtersArray.join(';'));
          } else {
            removeParameterFromUrl('filter');
          }
        }
      }
};